import { AbilityBuilder } from '@casl/ability';
import produce from 'immer';

import { OmniChannelShiftPermissions, ShiftNames } from 'packages/workspace/modules/algebra/constants';
import type { ITask } from 'packages/workspace/modules/algebra/interfaces/IOmniChannelPage';
import useObservable from 'packages/workspace/modules/helpers/hooks/useObservable';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { AppAbilities } from '../../../modules/helpers/contexts/abilities';
import { EventTypes } from '../../models/global-models';
import type { IOperator } from '../../models/operator';
import type { IOperatorProfile } from '../../models/operator-profile';
import type { Queue } from '../../models/queue';
import { AbilityStore } from '../abilities-store';
import merge from 'lodash.merge';
import { SoundOptions } from '../useSoundStore';
import { taskRouterStore } from './taskrouter-store';

export interface IShiftSession {
  access?: IOmniChannelPageAccess;
  voice_token: string;
  task_router_token: string;
  shift_begun?: boolean;
  error?: string;
  appId: string;
  operator: IOperator | null;
  shift_started: number;
  shift_id: string;
  queues: Array<Queue>;
  profile: IOperatorProfile | null;
  staffStatus: string;
  sessionId: string;
  pongUUID?: string;
}

export interface IOmniChannelPageAccess {
  support_centre: boolean;
  support_centre_staff: boolean;
  support_centre_staff_add_supervision: boolean;
  support_centre_staff_handle_task: boolean;
  support_centre_supervision: boolean;
  queue_mute_notification: boolean;
  queue_push_registration: boolean;
  queue_pickup_task: boolean;
  transfer_task: boolean;
  warm_handoff: boolean;
  swoop_in: boolean;
  group_chat: boolean;
  supervise: boolean;
  participant_request: boolean;
  operator_profile: Record<string, boolean>;
  queue_list: boolean;
}

export const initialState: IShiftSession = {
  operator: null as unknown as IOperator,
  access: {
    support_centre: false,
    support_centre_staff: false,
    support_centre_staff_add_supervision: false,
    support_centre_staff_handle_task: false,
    support_centre_supervision: false,
    queue_mute_notification: false,
    queue_push_registration: false,
    queue_pickup_task: false,
    transfer_task: false,
    warm_handoff: false,
    swoop_in: false,
    group_chat: false,
    supervise: false,
    operator_profile: {},
    queue_list: false
  },
  appId: '',
  shift_begun: false,
  shift_started: 0,
  voice_token: '',
  task_router_token: '',
  queues: [],
  shift_id: '',
  profile: null as unknown as IOperatorProfile,
  staffStatus: '',
  sessionId: '',
  pongUUID: ''
};

const shift$ = new BehaviorSubject(initialState);

export const shiftStore = {
  initialState,
  /**
   * Update Shift.
   * @param appId
   * @param operator
   * @param queues
   * @param profile
   * @param staffStatus
   * @param access
   * @param shitStarted
   */
  setHotlineShift: (
    appId: string,
    operator: IOperator,
    queues: Array<Queue>,
    profile: IOperatorProfile,
    staffStatus: string,
    access: IOmniChannelPageAccess,
    shitStarted: number = Date.now()
  ): void => {
    const rules = defineAbilitiesForOperator(access);
    AbilityStore.updateRules(rules, 'omnichannel');
    const state = produce(shift$.value, (draftState) => {
      draftState.appId = appId;
      draftState.operator = operator;
      draftState.queues = queues;
      draftState.shift_begun = true;
      draftState.shift_started = shitStarted;
      draftState.profile = profile;
      draftState.staffStatus = staffStatus;
      draftState.access = access;
    });
    shift$.next(state);
  },
  resetShiftInfo: (): void => {
    const state = produce(shift$.value, (draftState) => {
      draftState.operator = null;
      draftState.appId = '';
      draftState.shift_begun = false;
      draftState.shift_started = 0;
      draftState.voice_token = '';
      draftState.task_router_token = '';
      draftState.queues = [];
      draftState.profile = null;
      draftState.staffStatus = '';
    });
    const rules = revokeRulesForOperator();
    AbilityStore.updateRules(rules, 'omnichannel');
    shift$.next(state);
  },
  resetShitBegun: (): void => {
    const state = produce(shift$.value, (draffState) => {
      draffState.shift_begun = false;
    });
    shift$.next(state);
  },

  updateQueueMute(queueSid: string, mute: boolean): void {
    const state = produce(shift$.value, (draftState) => {
      draftState.queues.forEach((draftQueue) => {
        if (draftQueue.queue_sid === queueSid) {
          draftQueue.is_notification_mute = mute;
        }
      });
    });
    shift$.next(state);
  },
  updateQueueRotation(queueSid: string, rotation: boolean): void {
    const state = produce(shift$.value, (draftState) => {
      draftState.queues.forEach((draftQueue) => {
        if (draftQueue.queue_sid === queueSid) {
          draftQueue.is_push_registration = rotation;
        }
      });
    });
    shift$.next(state);
  },
  /**
   * Status Update
   */
  updateStatusAndProfile: (status: string, profile: IOperatorProfile, operator: IOperator, access: IOmniChannelPageAccess,queues: Array<Queue>,): void => {
    const rules = defineAbilitiesForOperator(access);
    AbilityStore.updateRules(rules, 'omnichannel');
    const updatedOperator = produce(operator, (draftState) => {
      draftState.operator_profile = profile;
    });
    const state = produce(shift$.value, (draftState) => {
      draftState.queues = queues;
      draftState.staffStatus = status;
      draftState.profile = profile;
      draftState.access = access;
      draftState.operator = merge(draftState.operator, updatedOperator);
    });
    shift$.next(state);
  },
  /**
   * New Activity in Queue
   * @param queueSid
   * @param task
   * @param event
   */
  updateQueueTasks: (queueSid: string, task: ITask, event: string): void => {
    //add in task of queue
    const state = produce(shift$.value, (draftState) => {
      draftState.queues.forEach((draftQueue) => {
        if (draftQueue.queue_sid === queueSid) {
          if (event === EventTypes.ADD_TASK || event === EventTypes.UPDATE_TASK || event === EventTypes.VISITOR_CONNECTED || event === EventTypes.VISITOR_DISCONNECTED) {
            if (!draftQueue.tasks) {
              draftQueue.tasks = {};
            }
            if (event === EventTypes.VISITOR_DISCONNECTED) {
              taskRouterStore.removeReservationWithSid(task.sid);
            }
            draftQueue.tasks[task.sid] = task;
          } else if (event === EventTypes.REMOVE_TASK && draftQueue?.tasks?.[task.sid]) {
            delete draftQueue?.tasks?.[task.sid];
          }
        }
      });
    });
    shift$.next(state);
    if (event === EventTypes.ADD_TASK && Boolean(shift$.value?.access?.supervise)) {
      const taskQueue = shift$.value.queues.find((each) => each.queue_sid === queueSid);
      if (taskQueue && !taskQueue.is_notification_mute) SoundOptions.setType(`supervisor_new_reservation_notification_${queueSid}`);
    }
  },
  updateStatus: (msg,queues: Array<Queue>) => {
    shiftStore.updateStatusAndProfile(msg.data?.activity, msg.data.operator_profile, msg.data, shift$.value.access,queues)
  },
  /**
   * Test For Blue Green
   */
  updateUUID: (id: string) => {
    const state = produce(shift$.value, (draftState) => {
      draftState.pongUUID = id;
    });
    shift$.next(state);
  },
  /*Update Operator Status  */
  updateUserStatus: (operator: IOperator) => {
    // Implementation for updating user status 
    const state = produce(shift$.value, (draftState) => {
      draftState.staffStatus = operator.activity;
      draftState.operator = operator;
    });
    shift$.next(state);
  }
};

/**
 * Hook Usage everywhere.
 */

export default function useShift(): IShiftSession {
  return useObservable<IShiftSession>(shift$, shift$.value);
}

/**
 * OmniChannel Access
 * @param access
 */
const defineAbilitiesForOperator = (access: IOmniChannelPageAccess) => {
  const existingRules = AbilityStore.getAbilities().rules;
  const support_centre_row_access = Object.keys(access?.operator_profile).filter(key => access?.operator_profile[key]);

  const { can: allow, rules, cannot: notAllow } = new AbilityBuilder(AppAbilities);
  if (access.support_centre) allow(OmniChannelShiftPermissions.SUPPORT_CENTRE, ShiftNames.OMNICHANNEL_SHIFT);
  if (!access.support_centre) notAllow(OmniChannelShiftPermissions.SUPPORT_CENTRE, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.support_centre_staff) allow(OmniChannelShiftPermissions.SUPPORT_CENTRE_STAFF, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.support_centre_supervision) allow(OmniChannelShiftPermissions.SUPPORT_CENTRE_SUPERVISION, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.support_centre_staff_handle_task) allow(OmniChannelShiftPermissions.SUPPORT_CENTRE_STAFF_HANDLE_TASK, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.support_centre_staff_add_supervision) allow(OmniChannelShiftPermissions.SUPPORT_CENTRE_STAFF_ADD_SUPERVISION, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.queue_mute_notification) allow(OmniChannelShiftPermissions.QUEUE_MUTE_NOTIFICATION, ShiftNames.OMNICHANNEL_SHIFT);
  if (!access.queue_mute_notification) notAllow(OmniChannelShiftPermissions.QUEUE_MUTE_NOTIFICATION, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.queue_push_registration) allow(OmniChannelShiftPermissions.QUEUE_PUSH_REGISTRATION, ShiftNames.OMNICHANNEL_SHIFT);
  if (!access.queue_push_registration) notAllow(OmniChannelShiftPermissions.QUEUE_PUSH_REGISTRATION, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.queue_pickup_task) allow(OmniChannelShiftPermissions.QUEUE_PICKUP_TASK, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.transfer_task) allow(OmniChannelShiftPermissions.TRANSFER_TASK, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.warm_handoff) allow(OmniChannelShiftPermissions.WARM_HANDOFF, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.swoop_in) allow(OmniChannelShiftPermissions.SWOOP_IN, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.group_chat) allow(OmniChannelShiftPermissions.GROUP_CHAT, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.supervise) allow(OmniChannelShiftPermissions.SUPERVISE, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.participant_request) allow(OmniChannelShiftPermissions.PARTICIPANT_REQUEST, ShiftNames.OMNICHANNEL_SHIFT);
  if (access.queue_list) allow(OmniChannelShiftPermissions.QUEUE_LIST, ShiftNames.OMNICHANNEL_SHIFT);
  allow(OmniChannelShiftPermissions.OPERATOR_PROFILE, ShiftNames.OMNICHANNEL_SHIFT, { alias: { $in: support_centre_row_access } });

  return [...existingRules.filter((iRule) => iRule.subject !== ShiftNames.OMNICHANNEL_SHIFT), ...rules];
};

const revokeRulesForOperator = () => {
  const existingRules = AbilityStore.getAbilities().rules;
  return existingRules.filter((iRule) => iRule.subject !== ShiftNames.OMNICHANNEL_SHIFT);
};
